import { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: 'max-w-full',
};

/**
 * Drawer: right side slide-over panel. Use it for create / edit flows and
 * record details so the list behind stays in context.
 *
 * - `header`: custom header node (replaces title/description).
 * - `footer`: sticky footer (Back / Next / Save buttons).
 * - `dismissible={false}` disables backdrop/Escape (e.g. during uploads).
 */
export default function Drawer({
  open,
  onClose,
  title,
  description,
  size = 'xl',
  header,
  footer,
  children,
  dismissible = true,
  hideClose = false,
  initialFocus,
  className,
  bodyClassName,
  padded = true,
  zIndex = 'z-[70]',
  eyebrow,
}) {
  const fallbackFocus = useRef(null);
  const handleClose = dismissible ? onClose : () => {};

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className={cn('relative', zIndex)} onClose={handleClose} initialFocus={initialFocus || fallbackFocus}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-[2px]" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-snappy duration-200"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-150"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className={cn('pointer-events-auto w-screen', sizes[size] || sizes.xl)}>
                  <div className={cn('flex h-full flex-col bg-white shadow-overlay sm:my-2 sm:mr-2 sm:h-[calc(100%-1rem)] sm:rounded-2xl sm:ring-1 sm:ring-gray-900/5 overflow-hidden', className)}>
                    <div className="flex shrink-0 items-start gap-4 border-b border-gray-100 px-5 py-4 sm:px-6">
                      <div className="min-w-0 flex-1">
                        {header ? (
                          header
                        ) : (
                          <>
                            {eyebrow && <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">{eyebrow}</p>}
                            {title && (
                              <Dialog.Title as="h2" className="text-base font-semibold leading-6 text-gray-900">
                                {title}
                              </Dialog.Title>
                            )}
                            {description && <Dialog.Description className="mt-1 text-sm text-gray-500">{description}</Dialog.Description>}
                          </>
                        )}
                      </div>
                      {!hideClose && (
                        <button
                          ref={initialFocus ? undefined : fallbackFocus}
                          type="button"
                          onClick={onClose}
                          className="-m-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      )}
                    </div>

                    <div className={cn('min-h-0 flex-1 overflow-y-auto', padded && 'px-5 py-5 sm:px-6', bodyClassName)}>{children}</div>

                    {footer && (
                      <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                        {footer}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
